/**
 * Stale-while-revalidate leaderboard reader — ported from the client half of
 * `icespace-web/src/services/LeaderboardSnapshot.ts` (the `fetch` / `fetchResult`
 * / cache / in-flight / non-poisoning-on-failure logic), generalized to:
 *  - be per-`gameId` (the icespace singleton read `leaderboard/snapshot`; here
 *    each game owns `games/{gameId}/leaderboard/snapshot`);
 *  - read through a {@link DocStorePort} instead of the raw Firestore SDK (no
 *    Firebase types leak in);
 *  - implement the platform {@link LeaderboardPort}.
 *
 * The two properties this preserves are the integrity requirements for this
 * domain:
 *  1. **Non-poisoning**: a FAILED read never overwrites a good cached snapshot
 *     with an empty one, and never marks the cache fresh — a later success (or
 *     a still-warm cache) must win.
 *  2. **Classified failure ≠ empty**: {@link LeaderboardSnapshotReader.fetchResult}
 *     distinguishes a genuinely-empty board (`error: null`) from a read failure
 *     (`error: 'denied' | 'offline' | 'unknown'`) so the UI never shows the
 *     "be the first to play!" card when the read actually broke.
 */
import {
  isValidGameId,
  leaderboardSnapshotPath,
  PlatformError,
  type DocStorePort,
  type GameId,
  type LeaderboardPort,
  type LeaderboardKind,
  type LeaderboardRow,
  type LeaderboardSnapshotDoc,
  type Logger,
} from '@djgg/core';

import { classifyLeaderboardError, type LeaderboardError } from './errors.js';

/** Result of a load that distinguishes failure from empty (parallel to
 *  `fetch()`'s empty-on-failure behavior for legacy callers). */
export interface LeaderboardLoadResult {
  rows: LeaderboardRow[];
  /** `null` on success (even for an empty board); a reason when the read failed. */
  error: LeaderboardError;
  /** When the served rows came from cache, when that cache was populated (ms). */
  computedAtMs: number | null;
  /**
   * Which board this is, as stamped by the recompute — readers label the rating
   * column from it, since a high-score table headed "Rating" reads as a bug.
   *
   * `null` means UNKNOWN, not Elo: no snapshot exists yet (an unplayed board
   * mints none by design) or the read failed. Callers that know the kind from
   * elsewhere should fall back to it — defaulting an absent board to Elo is how
   * a solo game ends up inviting players to a "Play online" duel it has no mode
   * for. A snapshot that predates the column IS an Elo ladder and reports so.
   */
  kind: LeaderboardKind | null;
}

/** How long a cached snapshot is served before a read revalidates it. */
export const DEFAULT_STALE_MS = 30_000;

export interface LeaderboardReaderOptions {
  /** SWR freshness window (ms). Default {@link DEFAULT_STALE_MS}. */
  staleMs?: number;
  /** Injected clock (test seam). Defaults to `Date.now`. */
  now?: () => number;
  /** Logger for swallowed read failures. */
  logger?: Logger;
}

interface CacheEntry {
  doc: LeaderboardSnapshotDoc;
  fetchedAt: number;
}

/**
 * Per-game SWR reader. One instance can serve every game (cache is keyed by
 * gameId). Implements {@link LeaderboardPort}: `fetch(gameId)` returns the
 * snapshot doc (empty on failure, for legacy callers); `fetchResult(gameId)`
 * exposes WHY a read produced no rows.
 */
export class LeaderboardSnapshotReader implements LeaderboardPort {
  private readonly store: DocStorePort;
  private readonly staleMs: number;
  private readonly now: () => number;
  private readonly log?: Logger;

  private readonly cache = new Map<GameId, CacheEntry>();
  private readonly inflight = new Map<GameId, Promise<LeaderboardLoadResult>>();
  private readonly lastError = new Map<GameId, LeaderboardError>();

  constructor(store: DocStorePort, opts: LeaderboardReaderOptions = {}) {
    this.store = store;
    this.staleMs = opts.staleMs ?? DEFAULT_STALE_MS;
    this.now = opts.now ?? (() => Date.now());
    this.log = opts.logger?.child('leaderboard');
  }

  /** {@link LeaderboardPort} surface — the snapshot doc, empty on failure. */
  async fetch(gameId: GameId): Promise<LeaderboardSnapshotDoc> {
    const result = await this.fetchResult(gameId);
    const cached = this.cache.get(gameId)?.doc;
    return {
      gameId,
      rows: result.rows,
      computedAtMs: cached?.computedAtMs ?? 0,
    };
  }

  /**
   * SWR load. Serves a fresh cached snapshot immediately; otherwise reads the
   * doc once (deduping concurrent callers via the in-flight map). On failure
   * the cache is NOT poisoned and the returned `error` carries the reason.
   */
  async fetchResult(gameId: GameId): Promise<LeaderboardLoadResult> {
    this.assertGameId(gameId);

    const entry = this.cache.get(gameId);
    if (entry && this.now() - entry.fetchedAt < this.staleMs) {
      return {
        rows: entry.doc.rows,
        error: null,
        computedAtMs: entry.doc.computedAtMs,
        kind: entry.doc.kind ?? 'elo',
      };
    }

    const existing = this.inflight.get(gameId);
    if (existing) {
      const r = await existing;
      // A concurrent caller shares the same in-flight read; surface the same
      // error it recorded rather than masking a failure as empty-success.
      return { ...r, error: this.lastError.get(gameId) ?? r.error };
    }

    const load = this.doLoad(gameId);
    this.inflight.set(gameId, load);
    try {
      return await load;
    } finally {
      this.inflight.delete(gameId);
    }
  }

  private async doLoad(gameId: GameId): Promise<LeaderboardLoadResult> {
    try {
      const doc = await this.store.getDoc<LeaderboardSnapshotDoc>(leaderboardSnapshotPath(gameId));
      // A snapshot that EXISTS but predates the kind column is an Elo ladder
      // (every board was one then). No snapshot at all leaves `kind` absent —
      // the board is unplayed, so its kind is genuinely unknown here.
      const resolved: LeaderboardSnapshotDoc = doc
        ? { gameId, rows: doc.rows ?? [], computedAtMs: doc.computedAtMs ?? 0, kind: doc.kind ?? 'elo' }
        : { gameId, rows: [], computedAtMs: 0 };
      this.cache.set(gameId, { doc: resolved, fetchedAt: this.now() });
      this.lastError.set(gameId, null);
      return {
        rows: resolved.rows,
        error: null,
        computedAtMs: resolved.computedAtMs,
        kind: resolved.kind ?? null,
      };
    } catch (e) {
      const error = classifyLeaderboardError(e);
      this.lastError.set(gameId, error);
      this.log?.warn(`snapshot fetch failed for ${gameId}: ${error}`, e);
      // Do NOT poison the cache with an empty snapshot on failure, and do NOT
      // mark it fresh — a later successful read (or the still-warm cache) wins.
      return { rows: [], error, computedAtMs: null, kind: null };
    }
  }

  /** Synchronous accessor — the most recently cached snapshot for a game, or null. */
  getCached(gameId: GameId): LeaderboardSnapshotDoc | null {
    return this.cache.get(gameId)?.doc ?? null;
  }

  /** Fire-and-forget warm at boot. Never throws. */
  prefetch(gameId: GameId): void {
    void this.fetchResult(gameId).catch(() => {});
  }

  /** Drop a game's cache so the next read revalidates (e.g. after a recompute). */
  invalidate(gameId: GameId): void {
    this.cache.delete(gameId);
  }

  private assertGameId(gameId: GameId): void {
    if (!isValidGameId(gameId)) {
      throw new PlatformError('validation', `invalid gameId: ${JSON.stringify(gameId)}`);
    }
  }
}
